import { AuthContextProvider, useAuth } from '@teko/auth-react';
import {
  generateTheme,
  LoadingProvider,
  ThemeProvider as DSThemeProvider,
} from '@teko/ds-desktop';
import { logger } from '@teko/log';
import { DeviceProvider } from '@teko/teko-ecommerce';
import { MegaMenuItemInterface } from '@teko/types/pb';
import { Theme as UiTheme, ThemeProvider } from '@teko/ui';
import { LanguageSelector } from 'components/desktop/organisms';
import MetaHead from 'components/mobile/organisms/MetaHead';
import { SmartBanner } from 'components/SmartBanner';
import {
  isHomepage,
  isServerless,
  setCookieByName,
  MockStorage,
  storageAvailable,
} from 'helpers';
import { translateObject } from 'helpers/translate';
import { usePos365StoreCode, useRouterEffect } from 'hooks';
import { changeLanguage, DEFAULT_LANGUAGE } from 'i18n';
import NextTracker from 'next-tracker';
import App, { AppContext, AppProps, NextWebVitalsMetric } from 'next/app';
import getConfig from 'next/config';
import Url, { Router, useRouter } from 'next/router';
import React, { useEffect, useState } from 'react';
import { renderTopBarMenu } from 'tenants/_template/desktop/_helpers';
import { parse as parseUrl } from 'url';
import {
  CartProviderV2,
  FeatureFlagProvider,
  FrameProvider,
  GlobalDataProvider,
  HelperProvider,
  HistoryProvider,
  initializeServices,
  LanguageProvider,
  NotificationProvider,
  PopularKeywordProvider,
  ScreenProvider,
  ServiceProvider,
  useGlobalData,
  LocationProvider,
} from '../contexts';
import { FlagState } from '../libs';
import { removePathTrailingSlash } from '../ports/next';
import { Area, BannerEntity, TenantEnv } from '../types';
import { ClientTenantEnvImpl } from './_env';
import layouts from './_layouts';
import routesTracking from './_routes';

const serverless = isServerless();

export interface MyAppProps extends AppProps {
  globalData: TenantEnv;
  banners: BannerEntity[];
  featureFlags: FlagState;
  menuItems?: MegaMenuItemInterface[];
  liteLogo: boolean;
  layoutAreas?: Area[];
}

interface LayoutProps {
  children: React.ReactNode;
  banners?: BannerEntity[];
  menuItems?: MegaMenuItemInterface[];
  liteLogo: boolean;
  layoutAreas?: Area[];
}

const Layout: React.FC<LayoutProps> = ({
  children,
  banners,
  menuItems,
  liteLogo,
  layoutAreas,
}: LayoutProps) => {
  const {
    isMobile,
    template,
    tenant,
    footerDataConfig = {},
    rightMenuTopBarButtonConfigs = [],
    leftMenuTopBarButtonConfigs = [],
    availableLanguages,
    requireLogin,
  } = useGlobalData();

  const { user } = useAuth();

  // load a language resource on the client
  useEffect(() => {
    if (serverless && window.track) {
      const userId = user?.userId || window.TekoID?.user?.getUserInfo()?.sub;
      if (userId) {
        window.track('setUserId', userId);
      }
    }
  }, [user?.userId, serverless && !!window.TekoID?.user?.getUserInfo()]);

  //@TODO: remove tenant in here
  const tpl = template || tenant;
  const defaultTemplate = 'vnshop';
  const footerData = translateObject(footerDataConfig);
  const rightMenu = renderTopBarMenu(
    translateObject(rightMenuTopBarButtonConfigs as object),
    !!availableLanguages ? [LanguageSelector] : [],
  );
  const leftMenu = renderTopBarMenu(
    translateObject(leftMenuTopBarButtonConfigs),
  );

  if (isMobile) {
    const MobileLayout =
      layouts.get(`${tpl}_mobile`) || layouts.get(`${defaultTemplate}_mobile`);
    return (
      <MobileLayout forceLogin={requireLogin} layoutAreas={layoutAreas}>
        {children}
      </MobileLayout>
    );
  } else {
    const DesktopLayout =
      layouts.get(`${tpl}_desktop`) ||
      layouts.get(`${defaultTemplate}_desktop`);
    return (
      <div className="minWidthWrapper" style={{ minWidth: '945px' }}>
        <DesktopLayout
          banners={banners}
          menuItems={menuItems}
          liteLogo={liteLogo}
          footerData={footerData}
          rightMenu={rightMenu}
          leftMenu={leftMenu}
          // hotfix the forceLogin for karavan
          // will refractor this in ECOM-362
          forceLogin={requireLogin}
          layoutAreas={layoutAreas}
        >
          {children}
        </DesktopLayout>
      </div>
    );
  }
};

const MyApp: React.FunctionComponent<MyAppProps> = ({
  Component,
  pageProps,
  globalData,
  banners,
  menuItems,
  featureFlags,
  liteLogo,
  layoutAreas,
}: MyAppProps) => {
  if (!globalData) {
    logger.error(
      `Empty global data when render ${Component.name}. Probably unable to detect tenant from hostname.`,
    );
    throw new Error('Missing global data when rendering component');
  }
  // alter localStorage and sessionStorage with a simple storage in case storages are not available
  if (typeof window !== 'undefined' && !storageAvailable('localStorage')) {
    Object.defineProperty(window, 'localStorage', {
      value: new MockStorage(),
    });
  }
  if (typeof window !== 'undefined' && !storageAvailable('sessionStorage')) {
    Object.defineProperty(window, 'sessionStorage', {
      value: new MockStorage(),
    });
  }

  // menuItems, banners and layoutAreas (custom page)
  // are fetched in Document.getInitialProps
  // and passed to MyApp in renderPage function.
  // For client-side transition,
  // get them from window object instead.
  banners = banners || (serverless ? window.banners : []);
  menuItems = menuItems || (serverless ? window.menuItems : []);
  layoutAreas = layoutAreas || (serverless ? window.layoutAreas : []);

  const { publicRuntimeConfig } = getConfig();

  const { tenant, isMobile, scopes, terminal, currentLanguage, trackingId } =
    globalData;

  // object fetched by getInitialProps does not
  // preserve function getProcessEnv when comes to client-side
  // re-initialize the object for that
  if (!globalData.getProcessEnv)
    globalData = new ClientTenantEnvImpl(globalData);

  const languageCode = currentLanguage || DEFAULT_LANGUAGE;
  if (!serverless) {
    changeLanguage(languageCode);
  }

  const router = useRouter();
  const { query } = router;

  const [history, setHistory] = useState([]);

  useEffect(() => {
    if (history[history.length - 1] !== router.asPath) {
      setHistory((prevState) => [...prevState, router.asPath]);
    }
  }, [router]);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  if (router && !(router as any).intercepted) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const customizedRouter = router as any;
    // the singleton router object created
    // intercept call to this method

    // this code ensure that when a next/link is clicked on the client-side
    // and navigation happens, the actual data fetched is prefixed with <tenant> path
    // Under the hood, next/link calls next/router to trigger page navigation
    // and it populates "as" field by default with the same value as "path";
    ['replace', 'push', 'prefetch'].forEach((method) => {
      const originalMethod = customizedRouter[method];

      customizedRouter[method] = (
        path: typeof Url,
        asPath: typeof Url,
        // arguments can be anything
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        ...args: any
      ) => {
        const { buildId } = window.__NEXT_DATA__;
        const defaultTenant = publicRuntimeConfig.defaultTenantDir;
        const { pathname } = parseUrl(
          typeof path === 'string' ? path : path.pathname,
        );
        if (pathname.match(/\/([\w\d\-]+)\/(mobile|desktop)(.*)/)) {
          return originalMethod.call(router, path, asPath, ...args);
        }
        const platform = isMobile ? 'mobile' : 'desktop';
        const pages = window.__BUILD_MANIFEST?.sortedPages || [];

        const customizedPath = removePathTrailingSlash(
          `/${tenant}/${platform}${pathname}`,
        );
        const defaultPath = removePathTrailingSlash(
          `/${defaultTenant}/${platform}${pathname}`,
        );
        let finalTenant = '';

        if (buildId === 'development') {
          finalTenant = pages.includes(customizedPath)
            ? tenant
            : pages.includes(defaultPath)
            ? defaultTenant
            : tenant;
        } else {
          finalTenant = pages.includes(customizedPath) ? tenant : defaultTenant;
        }

        const param =
          typeof path === 'string'
            ? removePathTrailingSlash(`/${finalTenant}/${platform}${path}`)
            : {
                ...path,
                pathname: removePathTrailingSlash(
                  `/${finalTenant}/${platform}${path.pathname}`,
                ),
              };

        asPath = asPath || path;
        return originalMethod.call(router, param, asPath, ...args);
      };
    });

    const originalMethod = customizedRouter['back'];

    // method call arguments can be anything
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    customizedRouter['back'] = (...args: any) => {
      if (!history.length) return router.push('/');
      return originalMethod.call(...args);
    };

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (router as any).intercepted = true;
  }

  useRouterEffect(router, globalData);
  usePos365StoreCode();

  useEffect(() => {
    const trackerAppId =
      globalData.getProcessEnv('TRACKER_APP_ID') || trackingId;
    if (trackerAppId) {
      new NextTracker({
        host: globalData.getProcessEnv('TRACKER_HOST_URL'),
        urlServeJsFile: globalData.getProcessEnv('TRACKER_SERVER_JS_URL'),
        appId: trackerAppId,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        routes: routesTracking as any,
        injectScript: false,
      });
      window.track('enableTrackVisibleContentImpressions');
      window.track('setTerminal', terminal);

      /**
       * because tracking is low priority so they should be
       * executed in idle cycle to avoid blocking user handler
       * TEMPORARILY COMMENT OUT BECAUSE IT CAUSING A BUG
       */
      // const trackOrigin = window.track;
      // window.track = function (...args: any[]) {
      //   function _track(deadline: IdleDeadline) {
      //     if (deadline.timeRemaining() > 0 || deadline.didTimeout) {
      //       if (args.length === 1) {
      //         const [event] = args;
      //         trackOrigin(event);
      //       } else if (args.length === 2) {
      //         const [event, data] = args;
      //         trackOrigin(event, data);
      //       } else {
      //         const [eventType, eventName, data] = args;
      //         trackOrigin(eventType, eventName, data);
      //       }
      //       return;
      //     }
      //     requestIdleCallback(_track);
      //   }
      //   requestIdleCallback(_track);
      // };

      if (serverless) {
        const originalFetch = window.fetch;
        window.fetch = function (...args) {
          return originalFetch
            .apply(this, args)
            .then(async function (data: Response) {
              if (data.status >= 400) {
                data
                  .clone()
                  .json()
                  .then((res: { code: string; message: string }) => {
                    window.track('error', {
                      errorSource: 'http',
                      apiCall: data.url,
                      apiPayload: JSON.stringify(args),
                      httpResponseCode: `${data.status}`,
                      responseJson: JSON.stringify(res),
                      errorCode: `${res.code}` || 'unknown',
                      errorMessage: res.message || 'unknown',
                    });
                  });
              }
              return data;
            });
        };
      }
    } else {
      if (serverless) {
        // polyfill with a dummy tracking function
        // to avoid undefined fn errors somewhere else
        window.track = () => {};
      }
    }

    saveUtmCookie(query);
    if (query?.referral) {
      setCookieByName(
        'referral',
        `${query.referral.toString()};SameSite=None;Secure;`,
      );
    }
  }, []);

  const services = initializeServices(globalData);
  const uiTheme = globalData.theme as UiTheme;
  const dsTheme = generateTheme({
    primary: uiTheme.brand.primary,
    secondary: uiTheme.brand.secondary,
  });
  dsTheme.colors = { ...dsTheme.colors, ...uiTheme.colors };

  return (
    <LanguageProvider code={languageCode}>
      <ThemeProvider theme={uiTheme}>
        <DSThemeProvider theme={dsTheme}>
          <LoadingProvider
            logo={(globalData.logo?.loading || globalData.logo?.lite)?.path}
          >
            <MetaHead
              theme={uiTheme}
              config={globalData}
              meta={globalData.meta}
            />
            <GlobalDataProvider value={globalData}>
              <ServiceProvider services={services}>
                <HelperProvider
                  discoveryPos365Service={services.discoveryPos365Service}
                  freeShippingThreshold={globalData?.freeShippingThreshold}
                >
                  <AuthContextProvider
                    partner={globalData.partner}
                    scopes={
                      scopes || [
                        'openid',
                        'profile',
                        'us',
                        'om',
                        'ppm',
                        'loyalty-consumer-bff',
                        'payment-consumer-bff',
                      ]
                    }
                    userService={services.userService}
                    authService={services.authService}
                    router={router}
                  >
                    <FeatureFlagProvider
                      flags={
                        featureFlags ||
                        (serverless ? window.featureFlags : null)
                      }
                    >
                      <LocationProvider>
                        <FrameProvider>
                          <PopularKeywordProvider>
                            <ScreenProvider>
                              <CartProviderV2>
                                <NotificationProvider>
                                  <DeviceProvider value={{ isMobile }}>
                                    <Layout
                                      banners={banners}
                                      menuItems={menuItems}
                                      liteLogo={liteLogo}
                                      layoutAreas={layoutAreas}
                                    >
                                      <HistoryProvider>
                                        <SmartBanner />
                                        <Component
                                          {...pageProps}
                                          menuItems={menuItems}
                                        />
                                      </HistoryProvider>
                                    </Layout>
                                  </DeviceProvider>
                                </NotificationProvider>
                              </CartProviderV2>
                            </ScreenProvider>
                          </PopularKeywordProvider>
                        </FrameProvider>
                      </LocationProvider>
                    </FeatureFlagProvider>
                  </AuthContextProvider>
                </HelperProvider>
              </ServiceProvider>
            </GlobalDataProvider>
          </LoadingProvider>
        </DSThemeProvider>
      </ThemeProvider>
    </LanguageProvider>
  );
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
(MyApp as any).getInitialProps = async (appContext: AppContext) => {
  // note this function is called every time
  // page transition happen,
  // so please think twice before adding any data-fetching logic
  // here.
  // Ideally, we would like to remove this function altogether.
  // But Nextjs's static optimization does not call
  // Document.getInitialProps for pages like /404,
  // which prevents the page from getting platform config.
  let globalTenantEnv;
  if (appContext.ctx.req) {
    // for pages that are SSR
    // this function is triggered on server-side
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    globalTenantEnv = (appContext.ctx.req as any).tenantEnv;
  } else {
    // for client-side only page, get this value from window
    // since req object is not set
    globalTenantEnv = window.__tenantConfig;
  }

  const liteLogo = !isHomepage(appContext.ctx.asPath);
  const appProps = await App.getInitialProps(appContext);
  return {
    ...appProps,
    globalData: omitServerFields(globalTenantEnv),
    liteLogo,
  };
};

// omit some fields without using in clients, ex: updatedUrlMap
function omitServerFields(tenantConfig: TenantEnv): Partial<TenantEnv> {
  const serverFields = new Set(['updatedUrlMap']);
  return Object.fromEntries(
    Object.entries(tenantConfig).filter(([k]) => !serverFields.has(k)),
  );
}

export function reportWebVitals(metric: NextWebVitalsMetric) {
  if (!serverless) return;

  if (metric.label === 'web-vital') {
    window.track?.('customEvent', 'web-vital', {
      category: metric.name,
      label: metric.label,
      value: Math.round(
        metric.name === 'CLS' ? metric.value * 1000 : metric.value, // values must be integers
      ),
    });
  }
}

export default MyApp;

const saveUtmCookie = (query: typeof Router.prototype.query) => {
  const {
    utm_source = '',
    utm_campaign = '',
    utm_term = '',
    utm_content = '',
    utm_medium = '',
    gclid = '',
    fbclid = '',
  } = query;

  const currentDate = new Date();
  const date = new Date(
    currentDate.getFullYear() + 2,
    currentDate.getMonth(),
    currentDate.getDay(),
  );
  const expireTime = date.toUTCString();

  if (utm_source) {
    setCookieByName('utmSource', utm_source as string, expireTime);
    setCookieByName('utmCampaign', utm_campaign as string, expireTime);
    setCookieByName('utmTerm', utm_term as string, expireTime);
    setCookieByName('utmContent', utm_content as string, expireTime);
    setCookieByName('utmMedium', utm_medium as string, expireTime);
  }

  if (gclid) setCookieByName('gclid', gclid as string, expireTime);
  if (fbclid) setCookieByName('fbclid', fbclid as string, expireTime);
};
